import { CSSProperties } from "react";
import { DialogProps } from "@material-ui/core/Dialog";

export type DialogMaxWidth = "xs" | "sm" | "md" | "lg" | "xl" | false;

/** Viewports below this width use mobile dialog behaviour (fullscreen when requested). */
export const MOBILE_BREAKPOINT = 501;

/** Standard max width for all desktop dialogs (Material-UI `md` = 960px). */
export const DESKTOP_DIALOG_MAX_WIDTH: DialogMaxWidth = "md";

/** Fixed paper width on desktop for standard (wide) dialogs. */
export const DESKTOP_DIALOG_WIDTH_PX = 960;

/** Material-UI default breakpoint widths. */
export const DIALOG_WIDTH_PX = {
  xs: 444,
  sm: 600,
  md: 960,
} as const;

/** Max width for admin entity setup forms (`maxWidth: false` in showDialog). */
export const DESKTOP_FORM_DIALOG_MAX_WIDTH_PX = 520;

export const DESKTOP_DIALOG_MIN_HEIGHT = "min(72vh, 640px)";

/** Shared frosted backdrop for app dialogs (main, auxiliary, and standalone). */
export const DIALOG_BACKDROP_STYLE: CSSProperties = {
  backgroundColor: "rgba(15, 23, 42, 0.45)",
  backdropFilter: "saturate(1.15) blur(10px)",
  WebkitBackdropFilter: "saturate(1.15) blur(10px)",
};

export const getDialogBackdropProps = (
  overrides?: DialogProps["BackdropProps"],
): DialogProps["BackdropProps"] => ({
  ...overrides,
  style: { ...DIALOG_BACKDROP_STYLE, ...overrides?.style },
});

/** Fixed title-bar content height (title + subtitle row); excludes safe-area top padding. */
export const DIALOG_HEADER_HEIGHT_PX = 64;

/** Close control size and inset from the top/right of the dialog chrome. */
export const DIALOG_HEADER_CLOSE_SIZE_PX = 40;
export const DIALOG_HEADER_CLOSE_OFFSET_PX = 8;

const isCompactDesktopWidth = (maxWidth?: DialogMaxWidth): maxWidth is "xs" | "sm" =>
  maxWidth === "xs" || maxWidth === "sm";

const desktopPaperStyle = (): CSSProperties => ({
  display: "flex",
  flexDirection: "column",
  width: DESKTOP_DIALOG_WIDTH_PX,
  maxWidth: "calc(100vw - 48px)",
  minHeight: DESKTOP_DIALOG_MIN_HEIGHT,
  maxHeight: "90vh",
});

const compactDesktopPaperStyle = (maxWidth: "xs" | "sm"): CSSProperties => ({
  display: "flex",
  flexDirection: "column",
  width: DIALOG_WIDTH_PX[maxWidth],
  maxWidth: "calc(100vw - 48px)",
  maxHeight: "90vh",
});

/** Shrink-wraps to form content (~500px); used for admin entity editors. */
const formDesktopPaperStyle = (): CSSProperties => ({
  display: "flex",
  flexDirection: "column",
  width: "auto",
  maxWidth: `min(${DESKTOP_FORM_DIALOG_MAX_WIDTH_PX}px, calc(100vw - 48px))`,
  maxHeight: "90vh",
  margin: 16,
});

export const isMobileViewport = (): boolean =>
  typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT;

export interface DialogLayoutOptions {
  maxWidth?: DialogMaxWidth;
  fullScreen?: boolean;
  widescreen?: boolean;
}

export interface DialogLayout {
  maxWidth: DialogMaxWidth;
  fullScreen: boolean;
  fullWidth: boolean;
  paperStyle?: CSSProperties;
}

/**
 * Resolves dialog dimensions.
 * - Desktop form (`false`): admin entity setup — shrink-wraps to content (~520px max)
 * - Desktop compact (`xs` / `sm`): fixed-width menus and pickers
 * - Desktop standard (`md` / `lg` / `xl`): leaderboards and content-heavy dialogs
 * - Widescreen / mobile fullscreen: unchanged
 */
export const getDialogLayout = (options: DialogLayoutOptions = {}): DialogLayout => {
  const isMobile = isMobileViewport();
  const widescreen = Boolean(options.widescreen);
  const wantsFullScreen = Boolean(options.fullScreen);
  const requestedWidth = options.maxWidth;

  if (widescreen || (wantsFullScreen && isMobile)) {
    return {
      maxWidth: false,
      fullScreen: true,
      fullWidth: true,
      paperStyle: {
        display: "flex",
        flexDirection: "column",
        maxHeight: "100%",
      },
    };
  }

  if (isMobile) {
    return {
      maxWidth: wantsFullScreen ? false : "sm",
      fullScreen: wantsFullScreen,
      fullWidth: true,
      paperStyle: wantsFullScreen
        ? { display: "flex", flexDirection: "column", maxHeight: "100%" }
        : { width: "100%", maxHeight: "calc(100vh - 32px)" },
    };
  }

  if (requestedWidth === false) {
    return {
      maxWidth: false,
      fullScreen: false,
      fullWidth: false,
      paperStyle: formDesktopPaperStyle(),
    };
  }

  if (isCompactDesktopWidth(requestedWidth)) {
    return {
      maxWidth: requestedWidth,
      fullScreen: false,
      fullWidth: true,
      paperStyle: compactDesktopPaperStyle(requestedWidth),
    };
  }

  return {
    maxWidth: DESKTOP_DIALOG_MAX_WIDTH,
    fullScreen: false,
    fullWidth: true,
    paperStyle: desktopPaperStyle(),
  };
};
